import { useState } from "react"
import Selector from "@/v2-backoffice-client/components/selector"
import type { SelectorTriggerProps } from "@/v2-backoffice-client/components/selector"
import { useCustomerQuery } from "@/v2-backoffice-client/customer/customer.query"

export type LicenseAddOnCustomerSelectorCustomer = {
  Id: number
  Name: string
}

export type LicenseAddOnCustomerSelectorProps = Omit<
  SelectorTriggerProps,
  "onSelect"
> & {
  /** The current bespoke customer; null = shared catalog. */
  selectedCustomerId: number | null
  onSelect(customer: LicenseAddOnCustomerSelectorCustomer | null): void
}

// Picks the customer a bespoke add-on is scoped to. The pinned first row is
// the scope-clearing choice — "shared catalog" is the default state, not a
// customer — and search works like every other customer picker (server-side,
// two characters on).
function LicenseAddOnCustomerSelector(
  props: LicenseAddOnCustomerSelectorProps,
) {
  const { selectedCustomerId, onSelect, ...triggerProps } = props

  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState("")

  const { data, loading, error } = useCustomerQuery({
    skip: !isOpen || search.length < 2,
    variables: {
      input: {
        limit: 10,
        filters:
          search.length >= 2
            ? [{ column: "Name", contains: search }]
            : undefined,
      },
    },
  })

  const results = data?.getCustomers.items ?? []

  return (
    <Selector onOpenChange={setIsOpen}>
      <Selector.Trigger iconSrc="user" {...triggerProps}>
        {triggerProps.children}
      </Selector.Trigger>

      <Selector.Content>
        <Selector.Search
          placeholder="Search by customer name..."
          onSearch={setSearch}
        />

        <Selector.List
          emptyLabel={
            search.length < 2
              ? "Type at least two characters to search"
              : "No customers found"
          }
          isLoading={loading}
          error={error}
          noResults={results.length === 0}
          pinned={
            <Selector.Item
              value="__catalog__"
              added={selectedCustomerId === null}
              onSelect={() => {
                onSelect(null)
              }}
            >
              <Selector.ItemText
                primary="Shared catalog"
                secondary="Grantable to any customer"
              />
            </Selector.Item>
          }
        >
          {results.map((customer) => {
            const customerName = customer.Name ?? `Customer ${customer.Id}`
            return (
              <Selector.Item
                key={customer.Id}
                value={String(customer.Id)}
                added={customer.Id === selectedCustomerId}
                onSelect={() => {
                  onSelect({ Id: customer.Id, Name: customerName })
                }}
              >
                <Selector.ItemText
                  primary={customerName}
                  secondary={`Bespoke — only ${customerName} may be granted it`}
                />
              </Selector.Item>
            )
          })}
        </Selector.List>
      </Selector.Content>
    </Selector>
  )
}

export default LicenseAddOnCustomerSelector
